import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { type OfflineModeManager } from 'OfflineMode';
import { type TranslationHelperClass } from 'Translation';
import { type DialogModal } from 'DialogModal';
import { type AnyObject } from '@Types';
import { type SafeApply } from 'SafeApply';
import { type FrontRoyalRootScope } from 'FrontRoyalAngular';
import { ArticleSiteId } from 'Resources/Resources.types';
import { angularInjectorProvider } from 'Injector';
import { reactRouterProvider } from 'FrontRoyalRouter/reactRouterProvider';
import { type ReactRouterBridge } from 'Navigation/angularModule/scripts/react_router_bridge';
import { storeProvider } from 'ReduxHelpers';
import { navigationActions } from 'Navigation/navigationSlice';
import { VALAR_DOMAIN, VALAR_DOMAIN_STAGING } from 'PedagoDomainConstants';
import { type ConfigFactory as ConfigFactoryType } from 'FrontRoyalConfig';
import { type LinkTarget } from './Navigation.types';
import { modifyHref, modifyTarget } from './linkHelpers';
import { type LoadRouteOptions } from './navigationHelper.types';

function loadRoute(path: string, options: LoadRouteOptions = {}) {
    reactRouterProvider.navigate(path, options);
}

type HardRedirectOptions = {
    replace?: boolean;
};

function doHardRedirect(url: string, options: HardRedirectOptions = {}) {
    const { replace = true } = options;

    // Store may not be initialized in some spec environments.
    storeProvider.store?.dispatch(navigationActions.startHardRedirect());

    if (replace) {
        window.location.replace(url);
    } else {
        window.location.assign(url);
    }
}

function hasPreviousHistoryEntry() {
    const historyState = window.history.state as { idx?: number } | null;
    return typeof historyState?.idx === 'number' ? historyState.idx > 0 : window.history.length > 1;
}

// We do not have a fully-featured history management system, but this is only used in a limited
// set of places (stream dashboard, mobile app header menu) and in all those cases this simple logic
// works: If there's history to go back to, go back. Otherwise, go home. (The go home is important, for example,
// if you started on a stream dashboard)
function goBack() {
    if (hasPreviousHistoryEntry()) {
        reactRouterProvider.navigate(-1);
        return;
    }

    goHome('navigationHelper.goBack');
}

function navigate(url: string, target: LinkTarget, features: string | undefined) {
    const $injector = angularInjectorProvider.requireInjector();
    const $rootScope = $injector.get<FrontRoyalRootScope>('$rootScope');
    const $window = $injector.get('$window');
    const safeApply = $injector.get<SafeApply>('safeApply');

    if (target === '_system' && Capacitor.isNativePlatform()) {
        AppLauncher.openUrl({ url }).catch(() => $window.open(url, target, features));
    } else {
        $window.open(url, target, features);
    }
    safeApply($rootScope);
}

function confirmNavigationAndNavigate(url: string, target: LinkTarget, features: string | undefined) {
    const $injector = angularInjectorProvider.requireInjector();
    const DialogModal = $injector.get<DialogModal>('DialogModal');
    const TranslationHelper = $injector.get<TranslationHelperClass>('TranslationHelper');
    const translationHelper = new TranslationHelper('navigation.navigation_helper');

    DialogModal.confirm({
        confirmCallback() {
            navigate(url, target, features);
        },
        title: translationHelper.get('you_are_offline'),
        text: translationHelper.get('still_want_to_navigate'),
        buttonContainerClass: 'center',
        confirmButtonClass: 'no-float',
        confirmButtonText: translationHelper.get('continue'),
        size: 'medium',
    });
}

function getUrlPrefix() {
    const $injector = angularInjectorProvider.requireInjector();
    const $window = $injector.get('$window');
    const reactRouterBridge = $injector.get<ReactRouterBridge>('reactRouterBridge');

    let urlPrefix;
    if ($window.CORDOVA && $window.CORDOVA.forcedUrlPrefix) {
        urlPrefix = $window.CORDOVA.forcedUrlPrefix;
    } else if (reactRouterBridge?.paramsRecord) {
        const params = reactRouterBridge.paramsRecord;
        urlPrefix = (params.url_prefix as string | undefined) ?? (params.urlPrefix as string | undefined);
    }

    urlPrefix = urlPrefix ? `${urlPrefix}/` : '';
    return urlPrefix;
}

// This code is really old, and I'm not sure if it's still valid. If someone passed a relative URL
// to loadUrl, this will prepend the appropriate domain to it. I don't think this even makes sense since
// we switched to having separate app and marketing domains. I don't think this works in the China region,
// so if we were using it we would have noticed that and updated it.
function modifyRelativeUrl(url: string): string {
    const $injector = angularInjectorProvider.requireInjector();
    const $window = $injector.get('$window');

    if (url.slice(0, 1) === '/') {
        if (getUrlPrefix().startsWith('miyamiya')) {
            return `https://miyamiya.org${url}`;
        }
        return $window.ENDPOINT_ROOT + url;
    }

    return url;
}

function loadUrl(
    url: string | null | undefined,
    target: LinkTarget = '_self',
    features: string | undefined = undefined,
) {
    const $injector = angularInjectorProvider.requireInjector();
    const offlineModeManager = $injector.get<OfflineModeManager>('offlineModeManager');

    if (!url) return undefined;

    // FIXME: we're changing modifyTarget so that target would default to _self
    // in the case of a non-external href. I don't think we pass non-external hrefs
    // here, or what we'd expect if we did. But think about it again
    target = modifyTarget(url, target);
    url = modifyRelativeUrl(url);
    url = modifyHref(url);

    if (offlineModeManager.inOfflineMode) {
        confirmNavigationAndNavigate(url, target, features);
        return undefined;
    }

    navigate(url, target, features);

    return undefined;
}

function loadFaqUrl(relativeUrl: string, target: LinkTarget = '_self', opts: { siteId?: string } & AnyObject = {}) {
    const $injector = angularInjectorProvider.requireInjector();
    const $rootScope = $injector.get<FrontRoyalRootScope>('$rootScope');
    const $window = $injector.get('$window');

    relativeUrl = relativeUrl.replace(/\/$/, '');

    // We use the site_id in the URL params on the server to figure out
    // what support site to redirect to. There may in instances, however, where
    // you want to link to a specific FAQ article that belongs to a particular
    // support site that isn't associated with the current user's relevant cohort.
    // In that case, you should use the article's unique path as the url rather
    // than the generalized alias version. Note, however, that the server will default
    // to the Quantic support site for unique FAQ article paths.
    //
    // NOTE: We expect there to be a currentUser here, but we're being defensive
    // just in case.

    const helpScoutArticleSiteId =
        opts.siteId ?? $rootScope.currentUser?.relevantCohort?.helpScoutArticleSiteId ?? ArticleSiteId.quantic;

    let url;
    try {
        const faqUrl = new URL(relativeUrl, $window.ENDPOINT_ROOT);
        faqUrl.searchParams.set('site_id', helpScoutArticleSiteId);
        url = faqUrl.pathname + faqUrl.search + faqUrl.hash;
    } catch {
        // If the URL constructed above isn't a valid URL, we do our best
        // effort to replicate that logic here.
        const search = `?site_id=${helpScoutArticleSiteId}`;
        const fragmentIndex = relativeUrl.indexOf('#');
        if (fragmentIndex === -1) {
            url = relativeUrl + search;
        } else {
            const pathname = relativeUrl.slice(0, fragmentIndex);
            const hash = relativeUrl.slice(fragmentIndex);
            url = pathname + search + hash;
        }
    }

    loadUrl(url, target);
}

function goHome(caller = 'unknown') {
    const $injector = angularInjectorProvider.requireInjector();
    const $rootScope = $injector.get<FrontRoyalRootScope>('$rootScope');
    $rootScope.goHome(caller);
    $rootScope.$apply();
}

// Sends the browser to the marketing homepage. NavigationHelperMixin delegates here.
function goToMarketingHome() {
    const $injector = angularInjectorProvider.requireInjector();
    const $window = $injector.get('$window');
    const urlPrefix = getUrlPrefix();

    if (urlPrefix.startsWith('valar')) {
        const ConfigFactory = $injector.get<ConfigFactoryType>('ConfigFactory');
        const config = ConfigFactory.getSync();

        if (config.appEnvType() === 'development') {
            // Ideally, we would send you to the Valar marketing homepage in the Gatsby project at 8001,
            // but our current development process doesn't require the Gatsby project to be running in
            // tandem with back royal, so there's no guarantee that the page is up and running. This hole
            // in our development process can be seen in other places of the app, so rather than try to
            // do the more correct thing here, we're just being consistent with the existing behavior
            // and sending you back to the Rails marketing homepage.
            $window.location.assign($window.location.origin);
        } else if (config.appEnvType() === 'staging') {
            $window.location.assign(`https://${VALAR_DOMAIN_STAGING}`);
        } else {
            $window.location.assign(`https://${VALAR_DOMAIN}`);
        }
        return;
    }

    $window.location.assign(`${$window.location.origin}/`);
}

export function navigationHelper() {
    return {
        getUrlPrefix,
        loadRoute,
        goBack,
        loadUrl,
        loadFaqUrl,
        goHome,
        goToMarketingHome,
        doHardRedirect,
    };
}
